/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"

import type { ApplicationDetailViewModel } from "../../features/application-detail/application-detail-viewmodel"
import { Card } from "../primitives/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../primitives/table"
import { FlagHistoryRow } from "./flag-history-row"
import { LoadMoreButton } from "./load-more-button"

interface Props {
    /**
     * View model that owns the loaded flag history pages and loads additional
     * pages.
     */
    vm: ApplicationDetailViewModel
}

/**
 * Displays the device's flag history, newest first, with incremental pagination.
 *
 * Each entry shows when the flag was set and removed, with an optional toggle
 * for its metadata. A flag can appear in multiple entries if it was set more
 * than once. Load Flags appends the next page and disappears when history is
 * fully loaded.
 *
 * @example
 * <TabsContent value="flags">
 *     <ApplicationDetailFlagHistory vm={vm} />
 * </TabsContent>
 */
export const ApplicationDetailFlagHistory = observer(({ vm }: Props) => {
    const flags = vm.applicationFlagHistory

    return (
        <div className="grid gap-4 border bg-background px-3 py-6">
            <h3 className="m-0 text-xl font-semibold">Flag History</h3>

            <Card className="py-0">
                {/* Disable row hover so an expanded entry doesn't highlight only half its content. */}
                <Table className="[&_tr]:hover:bg-transparent">
                    <colgroup>
                        <col className="w-1/3" />
                        <col className="w-1/3" />
                        <col className="w-1/3" />
                        {/* Let the toggle column size to its content. */}
                        <col className="w-0" />
                    </colgroup>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Flag</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead>Removed</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    {/* Each entry renders its own <tbody>; only the empty state needs one here. */}
                    {flags.length > 0 ? (
                        flags.map((flag) => (
                            // The same flag can have multiple history entries.
                            <FlagHistoryRow
                                key={`${flag.name}-${flag.timestampCreated}-${flag.timestampRemoved}`}
                                flag={flag}
                            />
                        ))
                    ) : (
                        <TableBody>{renderEmptyRow()}</TableBody>
                    )}
                </Table>
            </Card>

            {!vm.flagsLoaded && (
                <LoadMoreButton onLoad={() => vm.appendApplicationFlagHistory()}>Load Flags</LoadMoreButton>
            )}
        </div>
    )
})

/**
 * Renders the empty state when no flag history is available.
 *
 * It is also shown before the first page loads because the table does not
 * distinguish an empty result from an unloaded one.
 *
 * @returns A row spanning all four columns.
 */
function renderEmptyRow() {
    return (
        <TableRow>
            <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                No flag history
            </TableCell>
        </TableRow>
    )
}
